/**
 * <rendi-search-listing> — collection / search listing on the Rendi Search API
 * (ENG-266). Reads filters, sort and query from the URL (params mirror the API
 * 1:1, repeated keys for multi-value), calls the API, re-renders the grid from
 * the theme's card mould (<template data-card-template>) and loads the next page
 * when the sentinel scrolls into view. Every change pushes a URL; back/forward
 * re-query. On API failure the server-rendered grid stays and the UI is disabled.
 */
if (!customElements.get('rendi-search-listing')) {
  const INITIAL_ATTEMPT_TIMEOUT_MS = 3000;
  const INITIAL_API_TIMEOUT_MS = INITIAL_ATTEMPT_TIMEOUT_MS * 2;
  // pending -> ready | fallback (terminal API failure); native is server-only.
  // script-fallback is bootstrap-only and recovers to pending on initialization.
  const COLOR_SWATCHES = new Map([
    ['arancione', '--rendi-swatch-orange'],
    ['argento', '--rendi-swatch-silver'],
    ['azzurro', '--rendi-swatch-light-blue'],
    ['beige', '--rendi-swatch-beige'],
    ['bianco', '--rendi-swatch-white'],
    ['blu', '--rendi-swatch-blue'],
    ['giallo', '--rendi-swatch-yellow'],
    ['grigio', '--rendi-swatch-gray'],
    ['marrone', '--rendi-swatch-brown'],
    ['multicolor', '--rendi-swatch-multicolor'],
    ['nero', '--rendi-swatch-black'],
    ['oro', '--rendi-swatch-gold'],
    ['rosa', '--rendi-swatch-pink'],
    ['rosso', '--rendi-swatch-red'],
    ['verde', '--rendi-swatch-green'],
    ['viola', '--rendi-swatch-purple'],
  ]);
  const IMAGE_WIDTHS = [360, 480, 540, 720, 1080];
  const CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6"/></svg>';
  const CLOSE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  const esc = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Shopify money format ("€ {{amount_with_comma_separator}}") applied client-side.
  function formatMoney(cents, format) {
    const value = cents / 100;
    const render = (decimals, thousands, decimal) => {
      const [int, frac] = value.toFixed(decimals).split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return render(0, ',', '.');
        case 'amount_with_comma_separator': return render(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return render(0, '.', ',');
        case 'amount_with_apostrophe_separator': return render(2, "'", '.');
        default: return render(2, ',', '.');
      }
    });
  }

  function withWidth(url, width) {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.toString();
  }

  // "60 - 65 | 3 mesi" -> { age: "3 mesi", band: "60 - 65" }. The smallest band
  // has no pipe ("50 - 59 < 3 mesi") and reads as "0-3 mesi", like the finder.
  function sizeParts(label) {
    const sep = label.includes('|') ? '|' : label.includes('<') ? '<' : null;
    if (!sep) return null;
    const pieces = label.split(sep).map((part) => part.trim());
    // anything but exactly "band <sep> age" falls back to the raw label
    if (pieces.length !== 2 || !pieces[0] || !pieces[1]) return null;
    return { age: sep === '<' ? '0-' + pieces[1] : pieces[1], band: pieces[0] };
  }

  class RendiSearchListing extends HTMLElement {
    connectedCallback() {
      // Only exhausted API attempts are terminal. A bootstrap timeout has not
      // tried the API yet: recover while keeping the native grid visible.
      // native: a ?page=N page Liquid already rendered in full (PRO-262)
      if (this.dataset.initialState === 'fallback' || this.dataset.initialState === 'native') return;
      if (this.dataset.initialState === 'script-fallback') this.dataset.initialState = 'pending';
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      // the facet list is owned by the snippet config; price has its own keys
      this.facetKeys = this.config.facets.filter((f) => f.kind !== 'price').map((f) => f.key);
      this.strings = this.config.strings;
      this.gridWrap = this.querySelector('[data-grid-wrap]');
      this.grid = this.gridWrap.querySelector('.product-grid');
      this.cardTemplate = this.querySelector('[data-card-template]');
      this.filtersDialog = this.querySelector('[data-filters-dialog]');
      this.sortDialog = this.querySelector('[data-sort-dialog]');
      // bound here, where the references are (re)assigned: 'close' also fires
      // on Escape, so the body unlock cannot be missed
      [this.filtersDialog, this.sortDialog].forEach((dialog) => {
        dialog.addEventListener('close', () => {
          document.body.style.overflow = this.previousBodyOverflow || '';
        });
      });
      this.groupsNode = this.querySelector('[data-groups]');
      // two homes, same content: under the toolbar and inside the drawer
      this.chipsNodes = [...this.querySelectorAll('[data-chips]')];
      this.sortSelect = this.querySelector('[data-sort-select]');
      this.setupScrollRestoration();

      this.facets = {};
      this.priceBounds = null;
      this.cursor = null;
      this.loading = false;
      this.state = this.readUrl();

      this.renderSortControls();
      this.renderGroupShells();
      // the theme editor detaches and re-attaches the same instance
      if (!this.bound) {
        this.bindEvents();
        this.bound = true;
      }

      this.onPopState = () => {
        if (this.dataset.initialState === 'fallback') return;
        this.cancelScrollRestoration();
        this.state = this.readUrl();
        this.syncSortControls();
        this.classList.add('rendi-listing--loading');
        this.query(true);
      };
      window.addEventListener('popstate', this.onPopState);

      // A short first page leaves the sentinel in view before there is a cursor,
      // so the observer alone would never fire again: query() re-checks it.
      this.sentinelVisible = false;
      this.observer = new IntersectionObserver((entries) => {
        this.sentinelVisible = entries.some((entry) => entry.isIntersecting);
        this.loadMoreIfVisible();
      }, { rootMargin: '600px 0px' });
      this.observer.observe(this.querySelector('[data-sentinel]'));

      this.query(true);
    }

    disconnectedCallback() {
      this.cancelScrollRestoration();
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('load', this.onRestoreLoad);
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) => window.removeEventListener(type, this.onRestoreInteraction));
      window.removeEventListener('popstate', this.onPopState);
      this.observer?.disconnect();
      this.controller?.abort();
      clearTimeout(this.initialWatchdog);
      this.initialDeadline = null;
      // removing an open modal dialog never fires 'close'
      document.body.style.overflow = this.previousBodyOverflow || '';
    }

    // ---- URL <-> state -------------------------------------------------------
    setupScrollRestoration() {
      const saved = history.state?.rendiListingScroll;
      const navigation = performance.getEntriesByType('navigation')[0];
      // A bfcache return resumes the existing instance; only a rebuilt history
      // entry needs help after its short skeleton has clamped native scrolling.
      this.restorePosition = navigation?.type === 'back_forward' && saved?.url === location.href
        && Number.isFinite(saved.y) && saved.y > 0 ? saved : null;
      this.onPageHide = () => {
        try {
          history.replaceState({ ...history.state, rendiListingScroll: this.restorePosition || {
            url: location.href, x: scrollX, y: scrollY,
          } }, '');
        } catch (_) { /* Saving position must not prevent navigation. */ }
      };
      this.onRestoreLoad = () => this.restoreScroll();
      this.onRestoreInteraction = () => this.cancelScrollRestoration();
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('load', this.onRestoreLoad);
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) => window.addEventListener(type, this.onRestoreInteraction, { passive: true }));
    }

    cancelScrollRestoration() {
      this.restorePosition = null;
      cancelAnimationFrame(this.restoreFrame);
    }

    restoreScroll() {
      if (!this.restorePosition || this.loading || this.dataset.initialState === 'pending') return;
      cancelAnimationFrame(this.restoreFrame);
      this.restoreFrame = requestAnimationFrame(() => {
        const position = this.restorePosition;
        if (!position || !this.isConnected) return;
        const maxScroll = Math.max(0, document.documentElement.scrollHeight - innerHeight);
        if (maxScroll < position.y && this.cursor && this.dataset.initialState === 'ready') {
          // Rebuild only as many cursor pages as needed to reach the old position.
          this.query(false);
          return;
        }
        window.scrollTo({ left: position.x || 0, top: Math.min(position.y, maxScroll), behavior: 'instant' });
        // Native scroll restoration runs at load: reapply afterwards if the
        // API finished earlier. User interaction always cancels the correction.
        this.restoreFrame = requestAnimationFrame(() => {
          if (!this.restorePosition) return;
          // content-visibility can expand newly visible cards after scrolling;
          // let that layout settle before releasing the saved position.
          const target = Math.min(position.y, Math.max(0, document.documentElement.scrollHeight - innerHeight));
          if (Math.abs(scrollY - target) > 1) this.restoreScroll();
          else if (document.readyState === 'complete') this.cancelScrollRestoration();
        });
      });
    }

    readUrl() {
      const params = new URLSearchParams(window.location.search);
      const state = { facets: {}, price_min: null, price_max: null, sort: this.config.defaultSort, q: this.config.q || '' };
      this.facetKeys.forEach((key) => {
        state.facets[key] = params.getAll(key).map((v) => v.trim().toLowerCase()).filter(Boolean);
      });
      const min = parseInt(params.get('price_min'), 10);
      const max = parseInt(params.get('price_max'), 10);
      if (min >= 0) state.price_min = min;
      if (max >= 0) state.price_max = max;
      const sort = params.get('sort');
      if (this.config.sorts.some((option) => option.value === sort)) state.sort = sort;
      if (this.config.context === 'search' && params.has('q')) state.q = params.get('q');
      return state;
    }

    stateParams() {
      const params = new URLSearchParams();
      if (this.config.context === 'search' && this.state.q) params.set('q', this.state.q);
      this.facetKeys.forEach((key) => this.state.facets[key].forEach((value) => params.append(key, value)));
      if (this.state.price_min !== null) params.set('price_min', this.state.price_min);
      if (this.state.price_max !== null) params.set('price_max', this.state.price_max);
      if (this.state.sort !== this.config.defaultSort) params.set('sort', this.state.sort);
      return params;
    }

    commit() {
      this.cancelScrollRestoration();
      // instant feedback on a filter/sort change (never on first paint): the
      // grid dims until the new page lands
      this.classList.add('rendi-listing--loading');
      // foreign params (utm_*, fbclid...) survive; only our own get rewritten
      const params = new URLSearchParams(window.location.search);
      [...this.facetKeys, 'price_min', 'price_max', 'sort', 'q'].forEach((key) => params.delete(key));
      this.stateParams().forEach((value, key) => params.append(key, value));
      const query = params.toString();
      window.history.pushState(null, '', window.location.pathname + (query ? '?' + query : ''));
      this.query(true);
    }

    activeCount(key) {
      if (key === 'price') return this.state.price_min !== null || this.state.price_max !== null ? 1 : 0;
      return this.state.facets[key].length;
    }

    totalActive() {
      return this.facetKeys.reduce((sum, key) => sum + this.activeCount(key), 0) + this.activeCount('price');
    }

    // ---- API ----------------------------------------------------------------
    warmFallbackImages() {
      if (!this.grid) return;
      const columns = parseInt(getComputedStyle(this.grid).getPropertyValue('--product-columns-desktop'), 10) || 1;
      // Only the visible-width first row competes with the retry for bandwidth.
      [...this.grid.children].slice(0, columns).forEach((card) => {
        const image = card.querySelector('img');
        if (image) image.loading = 'eager';
      });
    }

    finishInitialLoad(state) {
      if (this.dataset.initialState !== 'pending') return;
      clearTimeout(this.initialWatchdog);
      this.dataset.initialState = state;
      this.querySelector('[data-skeleton]')?.remove();
      this.gridWrap.hidden = false;
      if (state === 'ready') {
        // The skeleton's shorter layout can leave a stale visible sentinel.
        // Ask for a fresh intersection before deciding to load the next page.
        this.sentinelVisible = false;
        const sentinel = this.querySelector('[data-sentinel]');
        this.observer.unobserve(sentinel);
        this.observer.takeRecords();
        this.observer.observe(sentinel);
      }
      if (state === 'fallback') {
        this.controller?.abort();
        this.setUnavailable(true);
        this.classList.remove('rendi-listing--loading');
        this.loading = false;
        this.restoreScroll();
      }
    }

    async fetchPage(url, signal, timeout) {
      const attempt = new AbortController();
      let timer;
      let onAbort;
      const aborted = new Promise((_, reject) => {
        onAbort = () => {
          attempt.abort();
          reject(new DOMException('Search request cancelled', 'AbortError'));
        };
        signal.addEventListener('abort', onAbort, { once: true });
        if (signal.aborted) onAbort();
        if (timeout !== null) {
          timer = setTimeout(() => {
            attempt.abort();
            reject(new Error('Search request timed out'));
          }, timeout);
        }
      });
      try {
        return await Promise.race([
          (async () => {
            const response = await fetch(url, { signal: attempt.signal });
            if (!response.ok) throw new Error('search api ' + response.status);
            const data = await response.json();
            if (!data || !Array.isArray(data.items)) throw new Error('Invalid search response');
            return data;
          })(),
          aborted,
        ]);
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
      }
    }

    async query(reset) {
      if (this.dataset.initialState === 'fallback') return;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.loading = true;
      if (reset) this.cursor = null;
      const params = this.stateParams();
      params.set('sort', this.state.sort);
      if (this.config.collectionId) params.set('collection', this.config.collectionId);
      if (this.cursor) params.set('cursor', this.cursor);
      const initial = this.dataset.initialState === 'pending';
      if (initial && !this.initialDeadline) {
        this.initialDeadline = performance.now() + INITIAL_API_TIMEOUT_MS;
        clearTimeout(this.initialWatchdog);
        this.initialWatchdog = setTimeout(() => this.finishInitialLoad('fallback'), INITIAL_API_TIMEOUT_MS);
      }
      try {
        let data;
        for (let attempt = 0; attempt < (initial ? 2 : 1); attempt++) {
          try {
            const timeout = initial ? Math.max(0, Math.min(INITIAL_ATTEMPT_TIMEOUT_MS, this.initialDeadline - performance.now())) : null;
            data = await this.fetchPage(this.config.endpoint + '?' + params.toString(), controller.signal, timeout);
            break;
          } catch (error) {
            if (controller.signal.aborted || !initial || attempt === 1) throw error;
            this.warmFallbackImages();
          }
        }
        if (controller.signal.aborted || controller !== this.controller) return;
        this.setUnavailable(false);
        this.cursor = data.nextCursor || null;
        this.renderGrid(data.items || [], reset);
        // cursor pages carry no facets/bounds (the API skips the aggregations:
        // same query, same numbers) — the first page's drawer stays
        if (reset) {
          this.facets = data.facets || {};
          this.priceBounds = data.price || null;
          this.total = typeof data.total === 'number' ? data.total : null;
          this.renderDrawer();
          this.renderToolbar();
        }
        if (initial) this.finishInitialLoad('ready');
        this.reloadWishlistIcons();
        if (controller === this.controller) {
          this.loading = false;
          this.restoreScroll();
          this.loadMoreIfVisible();
        }
      } catch (error) {
        if (controller.signal.aborted || controller !== this.controller) return;
        if (!initial) this.cancelScrollRestoration();
        // a failed first page disables the UI; a failed load-more keeps the
        // cursor, so scrolling the sentinel back into view retries instead of
        // silently ending the collection (no loadMoreIfVisible here: retrying
        // straight from the failure would hammer a failing API)
        if (reset) {
          this.setUnavailable(true);
          this.cursor = null;
          if (initial) this.finishInitialLoad('fallback');
        }
      } finally {
        // a superseded request must not touch the state of the one that replaced it
        if (controller === this.controller) {
          this.loading = false;
          this.classList.remove('rendi-listing--loading');
        }
      }
    }

    loadMoreIfVisible() {
      if (this.restorePosition) return;
      if (this.sentinelVisible && this.cursor && !this.loading) this.query(false);
    }

    setUnavailable(unavailable) {
      this.classList.toggle('rendi-listing--unavailable', unavailable);
      this.querySelector('[data-notice]').hidden = !unavailable;
      this.querySelectorAll('[data-open-filters], [data-open-sort], [data-sort-select]').forEach((el) => { el.disabled = unavailable; });
    }

    reloadWishlistIcons() {
      const wishlist = window.hulkappsWishlistCollection;
      if (wishlist && typeof wishlist._loadWishlistIcon === 'function') {
        try { wishlist._loadWishlistIcon(); } catch (e) { /* third-party app, never ours to break the grid over */ }
      }
    }

    // ---- grid -----------------------------------------------------------------
    renderGrid(items, reset) {
      if (!this.grid) {
        this.grid = document.createElement('div');
        this.grid.className = this.config.gridClass;
        this.gridWrap.replaceChildren(this.grid);
      }
      const cards = items.map((item) => this.buildCard(item));
      if (reset) this.grid.replaceChildren(...cards); else this.grid.append(...cards);
      // rendi-pdp.js listens for this to mirror the cart state on the quick-add buttons
      this.grid.dispatchEvent(new CustomEvent('rendi:cards-loaded', { bubbles: true }));
      this.querySelector('[data-empty]').hidden = !(reset && items.length === 0);
    }

    buildCard(item) {
      const card = this.cardTemplate.content.firstElementChild.cloneNode(true);
      // Keep the skeleton's background opaque while the first images load.
      if (this.dataset.initialState === 'pending') card.setAttribute('data-initial-card', '');
      const slot = (name) => card.querySelector(`[data-slot="${name}"]`);
      slot('link').href = window.Shopify.routes.root + 'products/' + item.handle;

      const image = slot('image');
      if (item.image_url) {
        image.src = withWidth(item.image_url, 720);
        image.srcset = IMAGE_WIDTHS.map((w) => withWidth(item.image_url, w) + ' ' + w + 'w').join(', ');
        image.alt = item.title;
      } else {
        image.remove();
      }

      // rendi-condition-label's collapse: "nuovo" keeps its badge, any other
      // condition shows the used one. No condition, no badge: those are the
      // catalog (non-used) products, which the server card leaves badge-less.
      const condition = (item.condition || '').trim();
      if (condition) slot('badge').textContent = condition === 'nuovo' ? this.config.badges.new : this.config.badges.used;
      else slot('badge').remove();

      if (item.brand && item.brand !== '-') slot('brand').textContent = item.brand; else slot('brand').remove();
      slot('title').textContent = item.title;
      slot('price').textContent = formatMoney(item.price_cents, this.config.moneyFormat);
      if (item.compare_at_cents > item.price_cents) slot('compare').textContent = formatMoney(item.compare_at_cents, this.config.moneyFormat);
      else slot('compare').remove();

      const fav = slot('fav');
      if (item.variant_id) {
        const heart = fav.querySelector('[data-gridwlhulklist]');
        heart.setAttribute('data-proID', item.id);
        heart.setAttribute('data-proHandle', item.handle);
        heart.setAttribute('data-varID', item.variant_id);
      } else {
        fav.remove();
      }

      // variant_id is the CHEAPEST variant, meant for the heart: quick-add must
      // never silently pick among several variants. variant_count is the real
      // gate (the server card's variants.size == 1); documents indexed before
      // the field existed fall back to the unique-used-piece heuristic
      // (condition set, single size).
      const add = slot('add');
      const sizes = Array.isArray(item.size) ? item.size : item.size ? [item.size] : [];
      const single = item.variant_count !== undefined ? item.variant_count === 1 : Boolean(condition) && sizes.length <= 1;
      if (item.variant_id && single) {
        add.setAttribute('data-variant-id', item.variant_id);
      } else {
        add.remove();
      }
      return card;
    }

    // ---- toolbar + sort -------------------------------------------------------
    renderToolbar() {
      const count = this.totalActive();
      this.querySelector('[data-filter-label]').textContent = count
        ? this.strings.filterButtonCount.replace('__COUNT__', count)
        : this.strings.filterButton;
    }

    renderSortControls() {
      this.sortSelect.innerHTML = this.config.sorts
        .map((option) => `<option value="${esc(option.value)}">${esc(option.label)}</option>`).join('');
      this.querySelector('[data-sort-list]').innerHTML = this.config.sorts
        .map((option) => `<label class="rendi-listing-radio"><span class="rendi-listing-radio__label">${esc(option.label)}</span><input type="radio" name="rendi-listing-sort" value="${esc(option.value)}"><span class="rendi-listing-radio__dot" aria-hidden="true"></span></label>`).join('');
      this.syncSortControls();
      this.renderToolbar();
    }

    syncSortControls() {
      this.sortSelect.value = this.state.sort;
      const radio = this.querySelector(`input[name="rendi-listing-sort"][value="${this.state.sort}"]`);
      if (radio) radio.checked = true;
      const current = this.config.sorts.find((option) => option.value === this.state.sort);
      this.querySelector('[data-sort-label]').textContent = current ? current.label : '';
    }

    // ---- drawer -----------------------------------------------------------------
    // The group shells are static (open/closed state lives in the <details>
    // itself); each response only rewrites their meta and body.
    renderGroupShells() {
      this.groupsNode.innerHTML = this.config.facets.map((facet) => `<details class="rendi-listing-group" data-group="${facet.key}">
        <summary class="rendi-listing-group__head"><span class="rendi-listing-group__label">${esc(facet.label)}</span><span class="rendi-listing-group__meta"><span data-group-meta></span>${CHEVRON}</span></summary>
        <div class="rendi-listing-group__body"></div>
      </details>`).join('');
    }

    // chips and group bodies are rewritten wholesale on every render, which
    // would drop the keyboard focus on <body>: remember the focused control by
    // its data-* attributes and find its replacement afterwards
    focusedSelector() {
      const el = document.activeElement;
      if (!this.filtersDialog.contains(el)) return '';
      return el.getAttributeNames().filter((n) => n.startsWith('data-'))
        .map((n) => `[${n}="${CSS.escape(el.getAttribute(n))}"]`).join('');
    }

    restoreFocus(selector) {
      if (!selector) return;
      // the control may be gone from the new result set or sit in a hidden
      // group: fall back to the drawer close button
      const el = this.filtersDialog.querySelector(selector);
      const target = el && !el.closest('[hidden]') ? el : this.filtersDialog.querySelector('[data-close]');
      target.focus({ preventScroll: true });
    }

    renderDrawer() {
      const focused = this.focusedSelector();
      this.renderChips();
      this.config.facets.forEach((facet) => {
        const group = this.groupsNode.querySelector(`[data-group="${facet.key}"]`);
        const count = this.activeCount(facet.key);
        // facets are disjunctive: a group with nothing to offer for this result
        // set (and nothing selected) is hidden rather than shown empty — and a
        // single-voice group (Categoria on a category page) hides too: per
        // design, one option is not a filter, even when it would technically
        // narrow the few results missing the attribute
        const useless = facet.kind === 'price'
          ? !this.priceBounds
          : (this.facets[facet.key] || []).length <= 1;
        group.hidden = useless && count === 0;
        group.querySelector('[data-group-meta]').textContent = count
          ? (count === 1 ? this.strings.activeOne : this.strings.activeOther).replace('__COUNT__', count)
          : '';
        const body = group.querySelector('.rendi-listing-group__body');
        const expanded = !!body.querySelector('.rendi-listing-options.is-expanded');
        if (facet.kind === 'price') {
          body.innerHTML = this.renderPrice();
          this.applyRange();
        } else if (facet.kind === 'toggle') {
          body.innerHTML = this.renderToggles(facet.key);
        } else {
          body.innerHTML = this.renderCheckboxes(facet.key);
          if (expanded) this.setExpanded(body, true);
        }
      });
      this.restoreFocus(focused);
    }

    renderToggles(key) {
      return `<div class="rendi-listing-toggles">${(this.facets[key] || []).map((value) => {
        const pressed = this.state.facets[key].includes(value.value);
        const swatch = key === 'color' ? COLOR_SWATCHES.get(value.value) : undefined;
        const sample = swatch ? `<span class="rendi-listing-swatch" style="background: var(${swatch})" aria-hidden="true"></span>` : '';
        return `<button type="button" class="rendi-listing-toggle" aria-pressed="${pressed}" data-facet="${key}" data-value="${esc(value.value)}">${sample}${esc(value.label)}</button>`;
      }).join('')}</div>`;
    }

    renderCheckboxes(key) {
      let values = this.facets[key] || [];
      // per design: brands read alphabetically, whatever order the API config has
      if (key === 'brand') values = [...values].sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));
      const rows = values.map((value) => {
        const checked = this.state.facets[key].includes(value.value);
        // Taglia reads age-first, cm band greyed in parentheses ("3 mesi (60 - 65 cm)")
        const parts = key === 'size' ? sizeParts(value.label) : null;
        const text = parts
          ? `${esc(parts.age)} <span class="rendi-listing-check__cm">(${esc(this.strings.sizeCm.replace('__BAND__', parts.band))})</span>`
          : esc(value.label);
        return `<li class="rendi-listing-option"><label class="rendi-listing-check"><input type="checkbox" data-facet="${key}" data-value="${esc(value.value)}"${checked ? ' checked' : ''}><span class="rendi-listing-check__box" aria-hidden="true"></span><span class="rendi-listing-check__label">${text}</span></label></li>`;
      }).join('');
      // the CSS hides rows past the sixth until the list is expanded
      const more = values.length > 6
        ? `<button type="button" class="rendi-listing-see-more" data-see-more="${key}" aria-expanded="false">${esc(this.strings.seeMore)}${CHEVRON}</button>`
        : '';
      return `<ul class="rendi-listing-options">${rows}</ul>${more}`;
    }

    setExpanded(body, expanded) {
      body.querySelector('.rendi-listing-options').classList.toggle('is-expanded', expanded);
      const button = body.querySelector('[data-see-more]');
      if (!button) return;
      button.setAttribute('aria-expanded', String(expanded));
      button.firstChild.textContent = expanded ? this.strings.seeLess : this.strings.seeMore;
    }

    // Slider bounds from the response, current values from the state; the
    // bounds stretch to hold a URL value outside them.
    priceRange() {
      const bounds = this.priceBounds;
      const floor = bounds ? Math.floor(bounds.min_cents / 100) : 0;
      const ceil = bounds ? Math.ceil(bounds.max_cents / 100) : 0;
      const lo = this.state.price_min !== null ? Math.round(this.state.price_min / 100) : floor;
      const hi = this.state.price_max !== null ? Math.round(this.state.price_max / 100) : ceil;
      return { floor: Math.min(floor, lo), ceil: Math.max(ceil, hi), lo, hi };
    }

    renderPrice() {
      const { floor, ceil } = this.priceRange();
      const input = (name, label) => `<label class="rendi-listing-input"><span class="rendi-listing-input__label">${esc(label)}</span><input class="rendi-listing-input__field" type="number" inputmode="numeric" min="${floor}" max="${ceil}" step="1" data-price="${name}"></label>`;
      const range = (name, label) => `<input type="range" min="${floor}" max="${ceil}" step="1" data-range="${name}" aria-label="${esc(label)}">`;
      return `<div class="rendi-listing-price">
        <div class="rendi-listing-price__inputs">${input('min', this.strings.priceFrom)}${input('max', this.strings.priceTo)}</div>
        <div class="rendi-listing-range">${range('min', this.strings.priceFrom)}${range('max', this.strings.priceTo)}</div>
      </div>`;
    }

    // Writes lo/hi to the inputs, the thumbs and the track fill; defaults to
    // the state.
    applyRange(lo, hi) {
      const range = this.priceRange();
      if (lo === undefined) ({ lo, hi } = range);
      const span = Math.max(range.ceil - range.floor, 1);
      const pct = (value) => ((value - range.floor) / span) * 100 + '%';
      const track = this.querySelector('.rendi-listing-range');
      track.style.setProperty('--lo', pct(lo));
      track.style.setProperty('--hi', pct(hi));
      this.querySelector('[data-range="min"]').value = lo;
      this.querySelector('[data-range="max"]').value = hi;
      this.querySelector('[data-price="min"]').value = lo;
      this.querySelector('[data-price="max"]').value = hi;
    }

    renderChips() {
      const chips = [];
      this.facetKeys.forEach((key) => {
        this.state.facets[key].forEach((selected) => {
          const match = (this.facets[key] || []).find((value) => value.value === selected);
          let label = match ? match.label : selected;
          if (key === 'size') {
            const parts = sizeParts(label);
            if (parts) label = parts.age;
          }
          chips.push({ key, value: selected, label });
        });
      });
      if (this.activeCount('price')) {
        const { lo, hi } = this.priceRange();
        chips.push({ key: 'price', value: '', label: `${formatMoney(lo * 100, this.config.moneyFormat)} - ${formatMoney(hi * 100, this.config.moneyFormat)}` });
      }
      const html = chips.map((chip) => `<span class="rendi-listing-chip"><span class="rendi-listing-chip__text">${esc(chip.label)}</span><button type="button" class="rendi-listing-chip__remove" data-remove-facet="${chip.key}" data-value="${esc(chip.value)}" aria-label="${esc(this.strings.removeFilter.replace('__LABEL__', chip.label))}">${CLOSE}</button></span>`).join('');
      this.chipsNodes.forEach((node) => {
        node.hidden = chips.length === 0;
        node.innerHTML = html;
      });

      // "N prodotti con i filtri attivi", under the page chips only; needs the
      // total the API exposes (older responses without it just hide the label)
      const countNode = this.querySelector('[data-result-count]');
      const showCount = chips.length > 0 && typeof this.total === 'number';
      countNode.hidden = !showCount;
      if (showCount) {
        const template = this.total === 1 ? this.strings.resultCountOne : this.strings.resultCountOther;
        const formatted = this.total.toLocaleString(document.documentElement.lang || 'it');
        countNode.innerHTML = esc(template).replace('__COUNT__', `<strong>${formatted}</strong>`);
      }

      // the drawer CTA carries the same total while filters are active
      const apply = this.querySelector('[data-apply]');
      if (showCount) {
        const applyTemplate = this.total === 1 ? this.strings.showProductsOne : this.strings.showProductsOther;
        apply.textContent = applyTemplate.replace(
          '__COUNT__',
          this.total.toLocaleString(document.documentElement.lang || 'it'),
        );
      } else {
        apply.textContent = this.strings.showProducts;
      }
    }

    // ---- state changes --------------------------------------------------------
    toggleValue(key, value) {
      const list = this.state.facets[key];
      const index = list.indexOf(value);
      if (index === -1) list.push(value); else list.splice(index, 1);
      this.commit();
    }

    removeFacet(key, value) {
      if (key === 'price') { this.state.price_min = null; this.state.price_max = null; }
      else this.state.facets[key] = this.state.facets[key].filter((v) => v !== value);
      this.commit();
    }

    clearAll() {
      this.facetKeys.forEach((key) => { this.state.facets[key] = []; });
      this.state.price_min = null;
      this.state.price_max = null;
      this.commit();
    }

    setSort(value) {
      if (!this.config.sorts.some((option) => option.value === value) || value === this.state.sort) return;
      this.state.sort = value;
      this.syncSortControls();
      this.commit();
    }

    // range thumbs drive the inputs and the fill live; the query runs on release
    syncRange(changed) {
      let lo = parseInt(this.querySelector('[data-range="min"]').value, 10);
      let hi = parseInt(this.querySelector('[data-range="max"]').value, 10);
      if (lo > hi) { if (changed === 'min') hi = lo; else lo = hi; }
      this.applyRange(lo, hi);
    }

    commitPrice() {
      const { floor, ceil } = this.priceRange();
      const read = (name) => parseInt(this.querySelector(`[data-price="${name}"]`).value, 10);
      let lo = read('min');
      let hi = read('max');
      if (Number.isNaN(lo)) lo = floor;
      if (Number.isNaN(hi)) hi = ceil;
      if (lo > hi) [lo, hi] = [hi, lo];
      // back at the bounds means no price filter at all
      this.state.price_min = lo > floor ? lo * 100 : null;
      this.state.price_max = hi < ceil ? hi * 100 : null;
      this.commit();
    }

    // iOS chains a sheet's touch scroll into the page behind it (the drawer's
    // footer rides away over a white void): the body locks while a dialog is
    // open, like every other drawer in the theme. The 'close' event also fires
    // on Escape, so the unlock cannot be missed.
    openDialog(dialog) {
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      dialog.showModal();
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const target = event.target;
        if (target.closest('[data-open-filters]')) { this.openDialog(this.filtersDialog); return; }
        if (target.closest('[data-open-sort]')) { this.openDialog(this.sortDialog); return; }
        if (target.closest('[data-close]')) { target.closest('dialog').close(); return; }
        if (target.closest('[data-clear]')) { this.clearAll(); return; }
        const toggle = target.closest('.rendi-listing-toggle');
        if (toggle) { this.toggleValue(toggle.dataset.facet, toggle.dataset.value); return; }
        const remove = target.closest('[data-remove-facet]');
        if (remove) { this.removeFacet(remove.dataset.removeFacet, remove.dataset.value); return; }
        const more = target.closest('[data-see-more]');
        if (more) { this.setExpanded(more.parentElement, more.getAttribute('aria-expanded') !== 'true'); return; }
        // click on the backdrop lands on the dialog element itself
        if (target instanceof HTMLDialogElement) target.close();
      });

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target.matches('input[type="checkbox"][data-facet]')) { this.toggleValue(target.dataset.facet, target.dataset.value); return; }
        if (target.matches('[data-sort-select]')) { this.setSort(target.value); return; }
        if (target.matches('input[name="rendi-listing-sort"]')) { this.sortDialog.close(); this.setSort(target.value); return; }
        if (target.matches('[data-range]')) { this.syncRange(target.dataset.range); this.commitPrice(); return; }
        if (target.matches('[data-price]')) { this.commitPrice(); }
      });

      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-range]')) this.syncRange(event.target.dataset.range);
      });
    }
  }

  customElements.define('rendi-search-listing', RendiSearchListing);
}
